import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useSummaryCardOrder } from '../hooks/useSummaryCardOrder';

export type SortableCard = {
  id: string;
  node: ReactNode;
  /** Pinned cards always render first, in declared order, and cannot be dragged. */
  pinned?: boolean;
  /** Human label used for the drag handle's accessible name. */
  label?: string;
};

type Props = {
  viewId: string;
  cards: SortableCard[];
  className?: string;
  ariaLabel?: string;
};

/** Reorders `ids` so `id` moves by `delta` slots, clamped to bounds. */
function moveId(ids: string[], id: string, delta: number): string[] {
  const from = ids.indexOf(id);
  if (from < 0) return ids;
  const to = Math.min(Math.max(from + delta, 0), ids.length - 1);
  if (to === from) return ids;
  const next = ids.slice();
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" fill="currentColor" />
      <circle cx="15" cy="6" r="1.6" fill="currentColor" />
      <circle cx="9" cy="12" r="1.6" fill="currentColor" />
      <circle cx="15" cy="12" r="1.6" fill="currentColor" />
      <circle cx="9" cy="18" r="1.6" fill="currentColor" />
      <circle cx="15" cy="18" r="1.6" fill="currentColor" />
    </svg>
  );
}

function EmptySlotIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="11" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
      <rect x="9" y="8" width="11" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" opacity="0.55" />
      <path d="M15 12h3M16.5 10.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Wraps a view's summary stat cards in a drag-and-drop reorderable grid whose
 * order is persisted per user and per view. Pinned cards (e.g. the Net worth
 * headline) are locked to the front; the rest can be reordered with the mouse
 * or keyboard. A dashed empty slot trails the cards to signal movability and
 * reserve space for a future "add card" affordance.
 */
export function SortableStatGrid({ viewId, cards, className, ariaLabel }: Props) {
  const { order, setOrder } = useSummaryCardOrder(viewId);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const gripRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const pinnedCards = useMemo(() => cards.filter((c) => c.pinned), [cards]);
  const movableCards = useMemo(() => cards.filter((c) => !c.pinned), [cards]);

  const orderedMovable = useMemo(() => {
    const byId = new Map(movableCards.map((c) => [c.id, c]));
    const result: SortableCard[] = [];
    const seen = new Set<string>();
    if (order) {
      for (const id of order) {
        const card = byId.get(id);
        if (card && !seen.has(id)) {
          result.push(card);
          seen.add(id);
        }
      }
    }
    for (const card of movableCards) {
      if (!seen.has(card.id)) result.push(card);
    }
    return result;
  }, [movableCards, order]);

  const orderedIds = useMemo(() => orderedMovable.map((c) => c.id), [orderedMovable]);

  // Refocus the moved grip after a keyboard reorder so navigation stays put.
  useEffect(() => {
    if (!pendingFocusId) return;
    gripRefs.current.get(pendingFocusId)?.focus();
    setPendingFocusId(null);
  }, [pendingFocusId, orderedIds]);

  const reorder = useCallback(
    (dragId: string, targetId: string | null) => {
      const without = orderedIds.filter((id) => id !== dragId);
      if (targetId === null) {
        setOrder([...without, dragId]);
        return;
      }
      const targetIndex = without.indexOf(targetId);
      if (targetIndex < 0) {
        setOrder([...without, dragId]);
        return;
      }
      const next = without.slice();
      next.splice(targetIndex, 0, dragId);
      setOrder(next);
    },
    [orderedIds, setOrder],
  );

  const handleDragStart = useCallback(
    (id: string) => (event: DragEvent<HTMLDivElement>) => {
      setDraggingId(id);
      event.dataTransfer.effectAllowed = 'move';
      try {
        event.dataTransfer.setData('text/plain', id);
      } catch {
        /* some browsers disallow setData outside user gesture */
      }
    },
    [],
  );

  const handleDragOver = useCallback(
    (id: string) => (event: DragEvent<HTMLDivElement>) => {
      if (!draggingId) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (id !== dropTargetId) setDropTargetId(id);
    },
    [draggingId, dropTargetId],
  );

  const handleDrop = useCallback(
    (targetId: string | null) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (draggingId && draggingId !== targetId) reorder(draggingId, targetId);
      setDraggingId(null);
      setDropTargetId(null);
    },
    [draggingId, reorder],
  );

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDropTargetId(null);
  }, []);

  const handleGripKeyDown = useCallback(
    (id: string) => (event: KeyboardEvent<HTMLButtonElement>) => {
      let delta = 0;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') delta = -1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') delta = 1;
      else return;
      event.preventDefault();
      const next = moveId(orderedIds, id, delta);
      if (next === orderedIds) return;
      setOrder(next);
      setPendingFocusId(id);
    },
    [orderedIds, setOrder],
  );

  return (
    <section className={className} aria-label={ariaLabel}>
      {pinnedCards.map((card, index) => (
        <div
          key={card.id}
          className="stat-card-drag stat-card-drag--pinned"
          style={{ ['--stat-card-index' as string]: index }}
        >
          {card.node}
        </div>
      ))}

      {orderedMovable.map((card, index) => {
        const isDragging = draggingId === card.id;
        const isDropTarget = dropTargetId === card.id && draggingId !== card.id;
        const wrapperClass = [
          'stat-card-drag',
          isDragging ? 'stat-card-drag--dragging' : '',
          isDropTarget ? 'stat-card-drag--drop-target' : '',
        ]
          .filter(Boolean)
          .join(' ');
        const label = card.label ?? card.id;
        const staggerIndex = pinnedCards.length + index;
        return (
          <div
            key={card.id}
            className={wrapperClass}
            style={{ ['--stat-card-index' as string]: staggerIndex }}
            draggable
            onDragStart={handleDragStart(card.id)}
            onDragOver={handleDragOver(card.id)}
            onDrop={handleDrop(card.id)}
            onDragEnd={handleDragEnd}
          >
            <button
              type="button"
              className="stat-card-grip"
              aria-label={`Reorder ${label}. Use arrow keys to move.`}
              title="Drag to reorder"
              ref={(el) => {
                if (el) gripRefs.current.set(card.id, el);
                else gripRefs.current.delete(card.id);
              }}
              onKeyDown={handleGripKeyDown(card.id)}
              onClick={(e) => e.preventDefault()}
            >
              <GripIcon />
            </button>
            {card.node}
          </div>
        );
      })}

      <div
        className={`stat-card-slot${draggingId ? ' stat-card-slot--active' : ''}`}
        style={{ ['--stat-card-index' as string]: pinnedCards.length + orderedMovable.length }}
        aria-hidden="true"
        onDragOver={handleDragOver('__slot__')}
        onDrop={handleDrop(null)}
      >
        <div className="stat-card-slot-inner">
          <EmptySlotIcon />
          <p className="stat-card-slot-label">Drag cards to reorder</p>
          <p className="stat-card-slot-hint">Custom metrics coming soon</p>
        </div>
      </div>
    </section>
  );
}
